/**
 * Agent users (roleID 13 / roleName "AGENT") work the Ocean Import module as their
 * outbound side, so its "Import" wording has to read "Export" for them.
 *
 * This is a display-only swap. Routes, API query params, payload keys and
 * redux state keep their import names so nothing on the backend changes.
 */
import { isAgentUser, readStoredAuthUser } from "@/utils/agentUser";

/**
 * Exact i18n keys that hold user-visible Ocean Import copy. The sidebar passes
 * the API's NavigationName straight to t(), hence the cased variants.
 */
const IMPORT_MODULE_KEYS = new Set([
    "import",
    "Import",
    "IMPORT",
    "importBooking",
    "importRateQuote",
    "importRateQuoteManagement",
    "importRateQuoteDescription",
]);

/** Key namespaces that belong entirely to the Ocean Import module. */
const IMPORT_MODULE_KEY_PREFIXES = ["oceanImport."];

/**
 * Whole-word only, so "Importer" / "Imported" keep their trade meaning and
 * only the module label itself flips.
 */
const LABEL_SWAPS = [
    [/\bImport\b/g, "Export"],
    [/\bIMPORT\b/g, "EXPORT"],
    [/\bimport\b/g, "export"],
];

/**
 * @param {string} text
 * @returns {string} the same text with Import wording swapped to Export
 */
export const toExportLabel = (text) => {
    if (typeof text !== "string" || !text) return text;
    return LABEL_SWAPS.reduce(
        (result, [pattern, replacement]) => result.replace(pattern, replacement),
        text,
    );
};

/** @param {string|string[]} key */
export const isImportModuleKey = (key) => {
    const keys = Array.isArray(key) ? key : [key];
    return keys.some(
        (candidate) =>
            typeof candidate === "string" &&
            (IMPORT_MODULE_KEYS.has(candidate) ||
                IMPORT_MODULE_KEY_PREFIXES.some((prefix) =>
                    candidate.startsWith(prefix),
                )),
    );
};

/**
 * Cached so the i18next post-processor stays synchronous and cheap — it runs on
 * every t() call. `useImportModuleLabels` keeps it in sync with the auth state.
 */
let agentLabelMode = isAgentUser(readStoredAuthUser());

export const getAgentLabelMode = () => agentLabelMode;

export const setAgentLabelMode = (enabled) => {
    agentLabelMode = Boolean(enabled);
};

export const AGENT_LABEL_POST_PROCESSOR = "agentImportLabels";

/** i18next post-processor that relabels Import-module copy for agent users. */
export const agentLabelPostProcessor = {
    type: "postProcessor",
    name: AGENT_LABEL_POST_PROCESSOR,
    process(value, key) {
        if (!agentLabelMode || !isImportModuleKey(key)) return value;
        return toExportLabel(value);
    },
};
